import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';

export enum IntegrationTypeEnum {
  AVTONET = 'avtonet',
  DOBER_AVTO = 'dober_avto',
  NJUSKALO = 'njuskalo',
  INDEX_HR = 'index_hr',
  FACEBOOK_DYNAMIC_CATALOG = 'facebook_dynamic_catalog',
  SHOPAJ = 'shopaj24',

  WEBSITE_DEVELOPMENT = 'website_development',
  INSTAGRAM_POSTS = 'instagram_posts',
  TIKTOK_POSTS = 'tiktok_posts',
  SNAPCHAT_POSTS = 'snapchat_posts',
  GOOGLE_BUSINESS = 'google_business',

  VIRTUAL_SALON = 'virtual_salon',
  SPACE_INVOICES = 'space_invoices',
  NLB_LEASING = 'nlb_leasing',

  /** @deprecated */
  AMP = 'amp',
  TRADING_HUB = 'trading_hub',
  TRADING_HUB_ADVERTISING = 'trading_hub_advertising',

  LEANPAY_LEASING = 'leanpay_leasing',
  PARTNERS_PORTAL = 'partners_portal',
  MOTIVE_SERVICE = 'motive_service',
  FACEBOOK = 'facebook',
  INTRIX_CRM = 'intrix_crm',
  BOLHA = 'bolha',
  ADDIKO = 'addiko',
}

export const IntegrationTypeFeatureFlagMapping = {
  'dober_avto': AccountFeaturesEnum.IS_AVAILABLE_DOBERAVTO,
  'njuskalo': AccountFeaturesEnum.IS_AVAILABLE_NJUSKALO,
  'index_hr': AccountFeaturesEnum.IS_AVAILABLE_INDEX_HR,
  'shopaj24': AccountFeaturesEnum.IS_AVAILABLE_SHOPAJ,
  'virtual_salon': AccountFeaturesEnum.ADVERTISEMENT_VIRTUAL_SALON,
  'facebook_dynamic_catalog': AccountFeaturesEnum.ADVERTISEMENT_SOCIAL_NETWORKS,
  'bolha': AccountFeaturesEnum.IS_AVAILABLE_BOLHA,
};

/**
 * Unlike the other entries in this map (and in the IntegrationsNames maps below), this is a
 * genuinely translatable UI label, not a brand name — defined once here so every consumer shows
 * it in the account's locale instead of a duplicated hardcoded Slovenian string.
 */
export const VIRTUAL_SALON_INTEGRATION_NAME = $localize`:@@integration.name.virtual-salon:Spletna stran`;

export const IntegrationsNames = {
  [IntegrationTypeEnum.DOBER_AVTO]: 'DoberAvto.si',
  [IntegrationTypeEnum.NJUSKALO]: 'Njuškalo.hr',
  [IntegrationTypeEnum.INDEX_HR]: 'Index.hr',
  [IntegrationTypeEnum.SHOPAJ]: 'Shopaj24.hr',
  [IntegrationTypeEnum.VIRTUAL_SALON]: VIRTUAL_SALON_INTEGRATION_NAME,
  [IntegrationTypeEnum.TRADING_HUB]: 'Trading Hub',
  [IntegrationTypeEnum.FACEBOOK_DYNAMIC_CATALOG]: 'Facebook Dynamic Catalog',
  [IntegrationTypeEnum.BOLHA]: 'Bolha',
};

/** BE `displayName` spells Virtual Salon untranslated in English; everything else it names is a brand. */
export function portalLabel(type: string, beName?: string | null): string {
  return type === IntegrationTypeEnum.VIRTUAL_SALON ? VIRTUAL_SALON_INTEGRATION_NAME : (beName || type);
}

/**
 * Portals connected once for the whole account, i.e. `integrations
 * .per_business_unit = 0` on the backend — DoberAvto and Quick Entry (Avtonet)
 * are the ones dealers notice. Their settings must never be opened in
 * business-unit scope: doing that showed DoberAvto's settings under a branch it
 * does not belong to (BRIEFD-7094).
 *
 * Keep in sync with the `per_business_unit` column. Where a component already
 * receives the flag from the API (the vehicle's advertising rows do), prefer
 * that over this list.
 */
export const ACCOUNT_SCOPED_INTEGRATIONS: string[] = [
  IntegrationTypeEnum.DOBER_AVTO,
  IntegrationTypeEnum.AVTONET,
  IntegrationTypeEnum.VIRTUAL_SALON,
  IntegrationTypeEnum.FACEBOOK_DYNAMIC_CATALOG,
];

/**
 * Portals whose settings live on their own page rather than a section of the
 * advertising settings page. Virtual Salon is a whole storefront, not an ad
 * feed, so its gear must land there and not on `?tab=virtual_salon`, which has
 * no section to show (BRIEFD-7094).
 */
export const INTEGRATION_SETTINGS_ROUTES: Record<string, string> = {
  [IntegrationTypeEnum.VIRTUAL_SALON]: '/admin/settings/integrations/virtual-salon',
};

/**
 * Every integration type that publishes a vehicle ad. Anything missing here is
 * invisible to the vehicle page's "active ads" carousel, which filters the
 * account's integrations through this list — Bolha was omitted, so a vehicle
 * live on Bolha reported "1 aktiven oglas" while the table below it showed two
 * (BRIEFD-7404 QA). Add new ad portals here as well as to IntegrationTypeEnum.
 */
export enum AdIntegrationTypeEnum {
  DOBER_AVTO = 'dober_avto',
  BOLHA = 'bolha',
  NJUSKALO = 'njuskalo',
  INDEX_HR = 'index_hr',
  SHOPAJ = 'shopaj24',
  FACEBOOK_DYNAMIC_CATALOG = 'facebook_dynamic_catalog',
  WEBSITE_DEVELOPMENT = 'website_development',
  INSTAGRAM_POSTS = 'instagram_posts',
  TIKTOK_POSTS = 'tiktok_posts',
  SNAPCHAT_POSTS = 'snapchat_posts',
}
